import { useState, useContext, useEffect, useMemo } from "react";
import { Box, Button, Checkbox, MenuItem, Modal, Typography, Grid, TextField, Autocomplete } from "@mui/material";
import { Link } from "react-router-dom";
import { CompanyContext } from "../context/companyContext";
import { Country, State, City } from "country-state-city";
import { Formik, Form, FormikProps } from "formik";
import { complainerDetailsSchema } from "../const/SchemaValidations";
import { InputTextField } from "./Formik Components/InputTextField";
import { createFilterOptions } from "@mui/material/Autocomplete";

interface ComplainerDetailsModalProps {
   open: boolean;
   setOpen: (open: boolean) => void;
   formProps: FormikProps<any>;
}

interface ICountry {
   name: string;
   isoCode: string;
}

interface IState {
   name: string;
   isoCode: string;
   countryCode: string;
}

interface ICity {
   name: string;
   stateCode: string;
   countryCode: string;
}

export const ComplainerDetailsModal = (props: ComplainerDetailsModalProps) => {
   const { company } = useContext(CompanyContext);
   const [isChecked, setIsChecked] = useState(false);

   useEffect(() => {
      if (props.open) setIsChecked(false);
   }, [props.open]);

   const countriesList = useMemo(() => Country.getAllCountries(), []);
   const menuPropsConfig = {
      disablePortal: true,
      disableScrollLock: true,
      anchorOrigin: {
         vertical: "bottom" as const,
         horizontal: "left" as const,
      },
      transformOrigin: {
         vertical: "top" as const,
         horizontal: "left" as const,
      },
      PaperProps: {
         style: {
            maxHeight: 250,
         },
      },
   };

   const filterOptions = createFilterOptions({
      matchFrom: "start",
      stringify: (option: any) => option.name,
   });

   return (
      <Modal
         open={props.open}
         onClose={() => props.setOpen(false)}
         sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
         disableEnforceFocus
      >
         <Box
            bgcolor="white"
            borderRadius={4}
            sx={{
               maxWidth: "90%",
               width: "50%",
               minWidth: 500,
               outline: "none",
               position: "relative",
            }}
         >
            <Box px={4} py={2}>
               <Typography variant="h5" fontWeight={600} letterSpacing={1}>
                  Ingresa tu información
               </Typography>
            </Box>

            <Formik
               initialValues={{
                  name: "",
                  lastName: "",
                  gender: "",
                  phoneNumber: "",
                  email: "",
                  address: "",
                  zipCode: "",
                  country: "",
                  state: "",
                  city: "",
               }}
               validationSchema={complainerDetailsSchema}
               onSubmit={(values, actions) => {
                  const countryName = Country.getCountryByCode(values.country)?.name || values.country;
                  const stateName = State.getStateByCodeAndCountry(values.state, values.country)?.name || values.state;

                  props.formProps.setValues({
                     ...props.formProps.values,
                     complaintType: "no anonima",
                     pseudonym: `${values.name} ${values.lastName}`,
                     followUpEmail: values.email,
                     complainerDetails: {
                        ...values,
                        country: countryName,
                        state: stateName,
                     },
                  });
                  actions.resetForm();
                  props.setOpen(false);
               }}
            >
               {(formProps: FormikProps<any>) => {
                  const { values, setFieldValue, errors, touched, handleBlur, handleChange } = formProps;
                  const statesList = values.country ? State.getStatesOfCountry(values.country) : [];
                  const citiesList =
                     values.country && values.state ? City.getCitiesOfState(values.country, values.state) : [];

                  return (
                     <Form>
                        <Box px={4} sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                           <Grid container spacing={1}>
                              <Grid item xs={6}>
                                 <InputTextField
                                    required
                                    fullWidth
                                    variant="standard"
                                    id="name"
                                    name="name"
                                    label="Nombre/s"
                                    type="text"
                                 />
                              </Grid>
                              <Grid item xs={6}>
                                 <InputTextField
                                    required
                                    fullWidth
                                    label="Apellidos"
                                    id="lastName"
                                    name="lastName"
                                    variant="standard"
                                    type="text"
                                 />
                              </Grid>
                              <Grid item xs={6}>
                                 <TextField
                                    select
                                    required
                                    fullWidth
                                    label="Género"
                                    id="gender"
                                    name="gender"
                                    variant="standard"
                                    value={values.gender}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.gender && Boolean(errors.gender)}
                                    helperText={touched.gender && (errors.gender as string)}
                                 >
                                    <MenuItem value={"masculino"}>Masculino</MenuItem>
                                    <MenuItem value={"femenino"}>Femenino</MenuItem>
                                    <MenuItem value={"indefinido"}>Indefinido</MenuItem>
                                 </TextField>
                              </Grid>
                              <Grid item xs={6}>
                                 <InputTextField
                                    fullWidth
                                    required
                                    label="Celular"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    variant="standard"
                                    type="text"
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <InputTextField
                                    required
                                    fullWidth
                                    label="Correo electrónico"
                                    id="email"
                                    name="email"
                                    variant="standard"
                                    type="email"
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <InputTextField
                                    fullWidth
                                    label="Dirección"
                                    id="address"
                                    name="address"
                                    variant="standard"
                                    type="text"
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <InputTextField
                                    fullWidth
                                    label="Código postal"
                                    id="zipCode"
                                    name="zipCode"
                                    variant="standard"
                                    type="text"
                                 />
                              </Grid>

                              <Grid item xs={12}>
                                 <Autocomplete<ICountry>
                                    id="country"
                                    options={countriesList}
                                    filterOptions={filterOptions}
                                    getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                                    value={countriesList.find((c) => c.isoCode === values.country) || null}
                                    onChange={(_event, newValue) => {
                                       const val = newValue && typeof newValue !== "string" ? newValue.isoCode : "";
                                       setFieldValue("country", val);
                                       setFieldValue("state", "");
                                       setFieldValue("city", "");
                                    }}
                                    renderInput={(params) => (
                                       <TextField
                                          {...params}
                                          label="País"
                                          variant="standard"
                                          fullWidth
                                          required
                                          error={touched.country && Boolean(errors.country)}
                                          helperText={touched.country && (errors.country as string)}
                                       />
                                    )}
                                 />
                              </Grid>

                              <Grid item xs={12}>
                                 <Autocomplete<IState>
                                    id="state"
                                    disabled={!values.country}
                                    options={statesList}
                                    filterOptions={filterOptions}
                                    getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                                    value={statesList.find((s) => s.isoCode === values.state) || null}
                                    onChange={(_event, newValue) => {
                                       const val = newValue && typeof newValue !== "string" ? newValue.isoCode : "";
                                       setFieldValue("state", val);
                                       setFieldValue("city", "");
                                    }}
                                    renderInput={(params) => (
                                       <TextField
                                          {...params}
                                          label="Estado"
                                          variant="standard"
                                          fullWidth
                                          required
                                          error={touched.state && Boolean(errors.state)}
                                          helperText={touched.state && (errors.state as string)}
                                       />
                                    )}
                                 />
                              </Grid>

                              <Grid item xs={12}>
                                 <Autocomplete<ICity>
                                    id="city"
                                    disabled={!values.state}
                                    options={citiesList}
                                    filterOptions={filterOptions}
                                    getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                                    value={citiesList.find((c) => c.name === values.city) || null}
                                    onChange={(_event, newValue) => {
                                       const val = newValue && typeof newValue !== "string" ? newValue.name : "";
                                       setFieldValue("city", val);
                                    }}
                                    renderInput={(params) => (
                                       <TextField
                                          {...params}
                                          label="Ciudad"
                                          variant="standard"
                                          fullWidth
                                          required
                                          error={touched.city && Boolean(errors.city)}
                                          helperText={touched.city && (errors.city as string)}
                                       />
                                    )}
                                 />
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                 <Box display="flex" alignItems="center">
                                    <Checkbox
                                       size="small"
                                       checked={isChecked}
                                       onChange={() => setIsChecked(!isChecked)}
                                    />
                                    <Typography variant="body2">
                                       He leído y acepto el&nbsp;
                                       <Link
                                          target={"_blank"}
                                          to={`/${company.subName}/aviso-de-privacidad`}
                                          style={{ textDecoration: "none", color: "#1976d2" }}
                                       >
                                          Aviso de Privacidad
                                       </Link>
                                    </Typography>
                                 </Box>
                              </Grid>
                           </Grid>
                        </Box>

                        <Box
                           bgcolor="#F1F1F1"
                           display="flex"
                           sx={{ borderBottomLeftRadius: 16, borderBottomRightRadius: 16, mt: 2 }}
                        >
                           <Button
                              variant="text"
                              sx={{ flex: 1, py: 2, color: "#d32f2f" }}
                              onClick={() => props.setOpen(false)}
                           >
                              Cancelar
                           </Button>
                           <Button sx={{ ml: 1, flex: 1, py: 2 }} variant="text" type="submit" disabled={!isChecked}>
                              Guardar
                           </Button>
                        </Box>
                     </Form>
                  );
               }}
            </Formik>
         </Box>
      </Modal>
   );
};
